<template>
  <v-card width="100%" class="summary light-border light-border-override" flat>
    <v-layout wrap>
      <v-flex xs12 sm12 md7 lg7 class="pa-4">
        <v-layout wrap align-center>
          <v-flex xs2 sm1 class="card-icon">
            <v-avatar size="50px" class="avatar">
              <v-img
                v-if="profileImage && profileImage.imageURL"
                :key="user.dateCreated"
                :src="`${profileImage.imageURL}?w=50&h=50&fit=crop`"
                alt="Profile Image"
                :lazy-src="lazyProfileImage"
              />
            </v-avatar>
          </v-flex>
          <v-flex v-if="user" xs10 sm11>
            <router-link
              :to="{
                name: 'userProfile',
                params: {
                  routeReloadRound: true,
                  userID: $route.params.userID,
                  id: $route.params.userID,
                },
              }"
            >
              <h2 :class="[$vuetify.theme.dark ? 'white--text' : 'black--text', 'player text-h5']">
                {{ user.displayName }}
              </h2>
            </router-link>
          </v-flex>

          <v-flex xs2 sm1 class="card-icon">
            <v-avatar size="50px">
              <v-icon>golf_course</v-icon>
            </v-avatar>
          </v-flex>
          <v-flex xs10 sm11 style="cursor: default">
            <div class="text-h6 font-weight-bold">
              {{ details.gameMode }}

              <router-link
                v-if="eventRound || eventRoundIneligible"
                :to="getEventRoute(details.game, eventRound, eventRoundIneligible)"
                style="vertical-align: top"
              >
                <span class="icon-indicator-wrapper">
                  <v-tooltip v-if="eventRound || eventRoundIneligible" top>
                    <template #activator="{ on }">
                      <v-avatar size="18" color="primary" class="icon-indicator" v-on="on">
                        <span class="white--text text-body-2">E</span>
                      </v-avatar>
                    </template>
                    <span>{{ eventName }} - {{ $t('roundDetails.event') }}</span>
                  </v-tooltip>
                </span>
                <span v-if="eventRoundIneligible">
                  <v-tooltip top>
                    <template #activator="{ on }">
                      <v-avatar size="18" color="info" class="icon-indicator" v-on="on">
                        <span class="white--text text-body-2">I</span>
                      </v-avatar>
                    </template>
                    <span>{{ $t('roundDetails.ineligiblePlayer') }}</span>
                  </v-tooltip>
                </span>
              </router-link>
              <span v-if="HCPRound" class="icon-indicator-wrapper">
                <v-tooltip top>
                  <template #activator="{ on }">
                    <v-avatar size="18" color="success" class="icon-indicator" v-on="on">
                      <span class="white--text text-body-2">H</span>
                    </v-avatar>
                  </template>
                  <span>Round Qualified for Handicap</span>
                </v-tooltip>
              </span>
            </div>
            <div class="created">{{ details.createdAt | utility_formatDateMY }}</div>
          </v-flex>

          <v-flex xs12 offset-xs1 sm8 offset-sm1>
            <div v-if="courseName" class="text-h6 text-center text-sm-left">{{ courseName }}</div>
          </v-flex>

          <v-flex xs12 sm3 class="text-right">
            <v-layout v-if="hasScorecard && showScorecard" wrap justify-space-around>
              <v-flex xs6 class="text-center right-border py-0">
                <v-flex class="round-stat-header py-0">Holes</v-flex>
                <transition name="fade" appear>
                  <v-flex class="round-stat user-handicap py-0">
                    {{ holeCount ? holeCount : '&dot;&dot;&dot;' }}
                  </v-flex>
                </transition>
              </v-flex>
              <v-flex xs6 class="text-center py-0">
                <v-flex class="round-stat-header py-0">Par</v-flex>
                <transition name="fade" appear>
                  <v-flex class="round-stat py-0">
                    {{ holeParCount ? holeParCount : '&dot;&dot;&dot;' }}
                  </v-flex>
                </transition>
              </v-flex>
            </v-layout>
            <v-layout
              v-else-if="!hasScorecard && score !== null && !showScorecard && !isPracticeRange"
              wrap
              justify-space-around
            >
              <v-flex xs7 class="text-center py-0">
                <v-flex class="round-stat-header py-0">Score{{ LDCheck ? '(yds)' : '' }}</v-flex>
                <transition name="fade" appear>
                  <v-flex v-if="KPCheck" class="round-stat py-0">
                    <span v-if="details.game === 'connect'">
                      {{ score ? utility_convertFeetInches(score) : '&dot;&dot;&dot;' }}
                    </span>
                    <span v-else>
                      {{
                        details && details.score_display ? details.score_display : '&dot;&dot;&dot;'
                      }}
                    </span>
                  </v-flex>
                  <v-flex v-else-if="LDCheck" class="round-stat py-0">
                    {{ score ? utility_roundNumYards(score) : '&dot;&dot;&dot;' }}
                  </v-flex>
                  <v-flex v-else class="round-stat py-0">
                    {{ score ? score : '&dot;&dot;&dot;' }}
                  </v-flex>
                </transition>
              </v-flex>
            </v-layout>
          </v-flex>

          <v-flex xs11 offset-xs1 class="py-6">
            <v-divider></v-divider>
          </v-flex>

          <v-flex xs11 offset-xs1 class="py- round-data">
            <v-layout wrap align-start justify-center fill-height>
              <v-flex xs12 text-sm-left sm4>
                <div v-if="details"><span>Players:</span> {{ details.players }}</div>
                <div v-if="details"><span>Tee Level:</span> {{ details.teeLevel }}</div>
                <div v-if="details && !isPracticeRange">
                  <span>Pin Position(s):</span> {{ details.pinPosition }}
                </div>
                <div v-if="details">
                  <span>AutoCaddie:</span> {{ details.autoCaddie | utility_yesNo }}
                </div>
                <div v-if="details && team"><span>Team:</span> {{ team }}</div>
              </v-flex>
              <v-flex xs12 text-sm-left sm4>
                <div v-if="roundHCP">
                  <span>Round Handicap:</span>
                  {{
                    roundHCP
                      | utility_toFixedOneDecimal
                      | utility_handicapPresence
                      | utility_handicapFormat
                  }}
                </div>
                <div v-if="details"><span>Putting Mode:</span> {{ details.puttingMode }}</div>
                <div v-if="details && details.mulligans > 0">
                  <span>Mulligans:</span> {{ details.mulligans }}
                </div>
                <div v-if="details">
                  <span>Mulligan Setting:</span> {{ details.mulliganSetting }}
                </div>
              </v-flex>
              <v-flex xs12 text-sm-left sm4>
                <div v-if="details"><span>Elevation:</span> {{ details.elevation }}</div>
                <div v-if="details"><span>Stimp Rating:</span> {{ details.stimpRating }}</div>
                <div v-if="details"><span>Green Hardness:</span> {{ details.greenHardness }}</div>
                <div v-if="details"><span>Wind:</span> {{ details.wind }}</div>
              </v-flex>
            </v-layout>
          </v-flex>
        </v-layout>
      </v-flex>

      <v-flex ref="courseImage" xs12 sm12 md5 lg5 class="pa-0 course-image">
        <v-card
          tile
          width="100%"
          flat
          darken-2
          class="white--text course-image-card event-course-card pa-0"
        >
          <v-img
            :src="courseImg"
            :aspect-ratio="aspect"
            alt="Course Image"
            class="course-image pa-0"
          />
        </v-card>
      </v-flex>
    </v-layout>
  </v-card>
</template>

<script>
import utilityMixin from '@/mixins/utilityMixin';
import { teamModes } from '@/lib/constants';
// Import utility functions directly (alternative to mixin)
import { utility_cmToFeetAndInches } from '@/utils/utilityFunctions';

export default {
  name: 'CourseCard',

  mixins: [utilityMixin],

  props: {
    details: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: () => {
        return {};
      },
    },
    scorecard: {
      type: Object,
      default: () => {
        return {};
      },
    },
    score: {
      type: Number,
      default: null,
    },
    isStrokePlay: {
      type: Boolean,
      default: false,
    },
    isScramble: {
      type: Boolean,
      default: false,
    },
    showScorecard: {
      type: Boolean,
      default: false,
    },
    presentedBy: {
      type: String,
      default: '',
    },
    gameMode: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      time: new Date(),
      viewWidth: document.body.clientWidth,
      viewHeight: document.body.clientHeight,
      aspect: 1.31,
      genericCourseImg:
        'https://e6golf-assets.imgix.net/courses/images/generic_course_placeholder.png',
      highWestTempImg:
        'https://e6golf-assets.imgix.net/courses/images/highwest_range_splash_ex.png',
      lofotenTempImg:
        'https://e6golf-qa-assets.imgix.net/uploads/courses/f88ccb50-6618-11ed-9022-0242ac120002/118/lofoten_0_splash.jpg',
      laBagnaiaTempImg:
        'https://e6golf-qa-assets.imgix.net/uploads/courses/3273aeb2-131d-4cfd-b52d-60b9652e3b15/120/labagnaia_0_splash.jpg',
      lazyProfileImage:
        'https://e6golf-qa-assets.imgix.net/shared/player-art/chipped.jpg?h=200&w=200&fit=crop',
      lazySrcImg:
        'https://e6golf-assets.imgix.net/uploads/clubhouses/branding/clubhouse-dashboard-header.jpg?blur=50',
      // missingSrcImg: 'https://e6golf-assets.imgix.net/courses/images/missing_course.jpg?blur=35',
      missingSrcImg:
        'https://e6golf-assets.imgix.net/courses/images/generic_course_placeholder.png',
    };
  },

  computed: {
    eventRound() {
      if (this.utility_isObject(this.details)) {
        // console.log('this.details==============:', this.details);
        if (this.details?.game === 'apex') {
          return this.details?.eventID ? this.details?.eventID : null;
        }
        return this.details?.eventID && this.details?.eventEligible === 'eligible'
          ? this.details?.eventID
          : null;
      }
      return null;
    },

    eventRoundIneligible() {
      if (this.utility_isObject(this.details)) {
        if (this.details?.game === 'apex') {
          return null;
        }
        return this.details?.eventID && this.details?.eventEligible !== 'eligible'
          ? this.details?.eventID
          : null;
      }
      return null;
    },

    HCPRound() {
      if (this.utility_isObject(this.details)) {
        return this.details?.qualifiedForHandicap ?? false;
      }
      return false;
    },

    courseName() {
      if (this.details) {
        return this.details?.course?.name;
      }
      return '';
    },

    holeCount() {
      if (this.details?.holeCountSelected) {
        return this.details.holeCountSelected;
      }

      if (this.scorecard?.holeScores) {
        return this.scorecard?.holeScores.reduce((acc, curr) => {
          if (curr) {
            acc += 1;
          }
          return acc;
        }, 0);
      }
      return 'N/A';
    },

    holeParCount() {
      if (this.scorecard?.holePars?.length && this.details?.selectedHoles?.length) {
        const newParTotal = this.scorecard.holePars.reduce((acc, curr) => {
          return acc + curr;
        }, 0);
        return newParTotal;

        // const pars = this.details.selectedHoles.reduce((acc, curr) => {
        //   console.log('acc:', acc);
        //   console.log('curr:', curr);
        //   const holePar = this.scorecard?.holePars?.[curr];
        //   console.log('holePar:', holePar);
        //   if (holePar) {
        //     acc += holePar;
        //   }
        //   return acc;
        // }, 0);

        // return pars;
      }
      return 'N/A';
    },

    courseImg() {
      if (
        this.details?.course?.image_url === 'image_url.png' &&
        this.details?.course?.name?.toLowerCase()?.includes('high west')
      ) {
        return `${this.highWestTempImg}?h=500&auto=format&fit=crop`;
      }
      if (
        (this.details?.course?.image_url === 'image_url.png' ||
          this.details?.course?.image_url === 'temp') &&
        this.details?.course?.name?.toLowerCase()?.includes('lofoten')
      ) {
        return `${this.lofotenTempImg}?h=500&auto=format&fit=crop`;
      }
      if (
        (this.details?.course?.image_url === 'image_url.png' ||
          this.details?.course?.image_url === 'temp') &&
        this.details?.course?.name?.toLowerCase()?.includes('bagnaia')
      ) {
        return `${this.labagnaiaTempImg}?h=500&auto=format&fit=crop`;
      }
      if (this.details?.course?.image_url && this.details?.course?.image_url?.includes('temp')) {
        return this.missingSrcImg;
      }
      if (this.details?.course?.image_url && this.details?.course?.image_url !== 'image_url.png') {
        return `${this.details.course.image_url}?auto=format&&fit=crop`;
      }
      return this.missingSrcImg;
      // return this.lazySrcImg;
    },

    profileImage() {
      return this.user?.profileImage ? this.user?.profileImage : '';
    },

    hasScorecard() {
      if (this.scorecard) {
        if (this.KPCheck || this.LDCheck) return false;
        return Object.keys(this.scorecard).length > 0 && this.scorecard.constructor === Object;
      }
      return false;
    },

    KPCheck() {
      if (this.gameMode) {
        return this.gameMode === 'closest_to_pin';
      }
      return false;
    },

    LDCheck() {
      if (this.gameMode) {
        return this.gameMode === 'longest_drive';
      }
      return false;
    },

    isPracticeRange() {
      if (this.gameMode) {
        return this.gameMode.includes('practice');
      }
      return false;
    },

    team() {
      const isTeam = teamModes?.includes(this.details.gameMode);
      if (this.isPracticeRange || !isTeam) return false;
      if (this.details?.team >= 0) {
        return this.details?.team + 1;
      }
      return false;
    },

    eventName() {
      if (this.utility_isObject(this.details)) {
        return this.details?.event?.name ?? '';
      }
      return 'N/A';
    },

    roundHCP() {
      if (this.utility_isObject(this.details)) {
        return this.details?.roundHandicap ?? this.user?.handicap;
      }
      return null;
    },
  },

  mounted() {
    if (this.$refs.courseImage) {
      this.aspect = this.$refs.courseImage.clientWidth / this.$refs.courseImage.clientHeight;
    }
    this.resizeHandler();
    this.$nextTick(() => {
      window.addEventListener('resize', this.resizeHandler);
      this.$on('hook:beforeDestroy', () => {
        window.removeEventListener('resize', this.resizeHandler);
      });
    });
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.resizeHandler);
  },

  methods: {
    utility_cmToFeetAndInches,

    getEventRoute(game, eventRound, eventRoundInelligle) {
      return game === 'connect' || !game
        ? `/events/${eventRound || eventRoundInelligle}`
        : `/apex_events/${eventRound}`;
    },

    resizeHandler() {
      if (this.$refs.courseImage) {
        this.aspect = this.$refs.courseImage.clientWidth / this.$refs.courseImage.clientHeight;
      }
      this.viewWidth = window.innerWidth;
      this.viewHeight = window.innerHeight;
    },
  },
};
</script>

<style scoped>
img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.container {
  min-height: calc(10vh - 65px) !important;
}

.summary {
  border-left: 10px solid var(--primary, #ba2127) !important;
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  overflow: hidden;
}

.light-border-override {
  border-bottom: none !important;
  border-bottom-right-radius: 0px;
  border-bottom-left-radius: 0px;
}

.course-image {
  max-height: 385px;
  min-height: 350px;
}

.card-icon-image {
  width: 30px;
  opacity: 0.6;
}

.theme--dark .card-icon-image {
  opacity: 1;
}

.theme--dark .card-icon {
  background-size: 50%;
  background-position: 10px 8px;
  opacity: 0.9;
}

.avatar img {
  object-fit: contain;
  box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.5);
}

.created {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.4);
  font-style: italic;
}

.round-stat-header {
  font-size: 0.875rem;
  color: var(--grey-light, #95989b);
}

.round-stat {
  font-size: 1.313rem;
}

.right-border {
  border-right: 1px solid var(--grey-lightest, #dddddd);
}

.round-data {
  font-size: 0.9375rem;
  min-height: 100px;
}

.round-data span {
  color: rgba(0, 0, 0, 0.54);
}

.theme--dark .created,
.theme--dark .round-stat-header,
.theme--dark .round-data span {
  color: rgba(255, 255, 255, 0.8);
  color: var(--grey-lightest);
}

.icon-indicator-wrapper {
  display: inline-block;
  max-width: 18px !important;
  max-height: 18px !important;
  vertical-align: top;
}

.icon-indicator {
  max-width: 18px !important;
  max-height: 18px !important;
}

@media (max-width: 1280px) and (min-width: 960px) {
  .avatar {
    height: 40px !important;
    width: 40px !important;
    min-width: 40px !important;
  }
}

@media (max-width: 1024px) {
  .course-image {
    max-height: 40vh;
  }
}

@media (max-width: 600px) {
  .course-image {
    max-height: 20vh;
    min-height: 100px;
  }
  .avatar {
    height: 40px !important;
    width: 40px !important;
    min-width: 40px !important;
  }
}
</style>
